// Boot-time designer preload: resolve a product from the live catalog and ghost-init the SDS SDK.

import { store } from '../../core/store.js'
import { eventBus, EVENTS } from '../../core/event-bus.js'
import { createLogger } from '../../utils/logger.js'
import { loadSDK } from './sdk-loader.js'
import { initDesigner } from './sds/sdk-init.js'
import { CONFIG } from '../../core/config.js'
import { getSessionId } from '../../api/index.js'

const log = createLogger('design')

// No hardcoded default product. The old '38856' constant pointed at a product
// that isn't in the listed catalog, so initializing the SDK with it produced a
// broken canvas + an id-less gateway 400. When no product can be resolved from
// the live catalog, we skip auto-init and prompt the user to pick one instead.
const DEFAULT_PRODUCT_ID = null

/**
 * Background preload — load the SDS SDK and init the canvas with the DEFAULT
 * product as soon as the app boots (while Create mode is open), so switching
 * to Design is instant instead of lazy-initializing on first click
 * (bao 2026-06-05; contract: docs/best-practice/03_sds-designer-sdk-contract.md).
 *
 * Idempotent. Safe while #sds-container is display:none — the SDK sizes from
 * the explicit previewSize param and getPreviewSize() falls back when the
 * container measures 0. On failure it resets so Design-mode activate() can
 * retry the normal lazy path.
 */
/**
 * Resolve the boot-preload product from the live designer catalog (first
 * listed item) so the store carries the FULL product payload — name, FRONT
 * selling price, variants. The old hardcoded DEFAULT_PRODUCT_ID gave the
 * store a bare {id}: no price -> Share&Buy's Shopify creation refused with
 * "no valid price" (and 38856 isn't even in the listed catalog). Returns null
 * when the catalog yields nothing — callers must skip auto-init in that case.
 */
export async function resolvePreloadProduct() {
  // ?product=<sds_id> (Shopify product-page modal deep-link) pre-selects that
  // product into the canvas; without it we fall back to the first listed item.
  const wanted = new URLSearchParams(window.location.search).get('product')
  try {
    const sessionId = getSessionId()
    // ?product=<sds_id> → exact backend lookup via &sds_id=. The old approach
    // pulled limit=200 and find()'d client-side, which (a) 500'd on the
    // designer scene (too many SQL variables once related-ends were attached)
    // and (b) silently missed any base past page 1 — the catalog is 350+, not
    // "small", so a fixed limit can never cover it. The backend now returns the
    // single matching base directly. Without ?product= we just take the first
    // listed base for boot preload.
    const qs = wanted
      ? `scene=designer&sds_id=${encodeURIComponent(wanted)}&limit=1&session_id=${sessionId}`
      : `scene=designer&limit=1&session_id=${sessionId}`
    const res = await fetch(`${CONFIG.FRONTEND_API}/api/customer/base-products?${qs}`)
    const result = await res.json()
    const products = result.products ?? []
    const p = products[0]
    if (p?.sds_id) {
      return {
        id: p.sds_id,
        name: p.sds_par_english_name || p.name || 'Product', // legacy-ref-ok
        price: p.current_price || p.price || p.mms_back_end_price_usd, // legacy-ref-ok
        variants: p.variants,
        thumbnail: p.thumbnail || p.thumbnail_url,
        // SDS production material-size requirement — consumed by add-to-design's
        // picture_request gate (sds-data-model.md §6).
        picture_request: p.picture_request,
      }
    }
  } catch (err) {
    log.warn('Preload catalog fetch failed; no resolvable product:', err?.message || err)
  }
  // No hardcoded fallback (DEFAULT_PRODUCT_ID is null). Callers must handle a
  // null return by skipping auto-init / prompting the user to pick a product.
  return null
}

let preloadStarted = false

/**
 * Preload the SDS SDK + default-product canvas in the background (see the
 * background-preload rationale block above). Idempotent; resets on failure so
 * Design-mode activate() can retry the normal lazy path.
 */
export async function preloadDesigner() {
  if (preloadStarted || store.getState('design.isSDKReady')) return
  preloadStarted = true

  // Ghost-render the design panel while preloading: the SDK measures its
  // containers at init, and a display:none ancestor yields 0 sizes → a
  // mis-scaled canvas once shown. preload-ghost = measurable but invisible.
  const modePanel = document.getElementById('mode-design')
  const sdsContainer = document.getElementById('sds-container')
  const ghosted = !!(modePanel && !modePanel.classList.contains('active'))
  if (ghosted) {
    modePanel.classList.add('preload-ghost')
    if (sdsContainer) sdsContainer.style.display = 'flex'
  }

  try {
    if (!store.getState('design.isSDKLoaded')) {
      await loadSDK()
    }
    let current = store.getState('design.currentProduct')
    if (!current?.id) {
      current = await resolvePreloadProduct()
      if (current?.id) store.setState('design.currentProduct', current)
    }
    // No resolvable product → don't init the SDK with an invalid id. Design
    // mode's activate() will show the product picker on first entry instead.
    if (!current?.id) {
      log.info('Designer preload skipped — no product to initialize; user will pick one')
      preloadStarted = false
      return
    }
    await initDesigner(current.id)
    // Keep the ghost up until the SDK finishes layout (initCallback can take
    // >10s); bounded so a wedged init can't leave a phantom panel forever.
    await new Promise((resolve) => {
      if (store.getState('design.isSDKReady')) return resolve()
      const t = setTimeout(resolve, 30_000)
      eventBus.once(EVENTS.SDK_READY, () => {
        clearTimeout(t)
        resolve()
      })
    })
    log.info('Designer preloaded — default product canvas ready')
  } catch (error) {
    preloadStarted = false
    log.warn('Designer preload failed (will lazy-init on Design entry):', error?.message || error)
  } finally {
    if (ghosted) {
      modePanel.classList.remove('preload-ghost')
      // Re-hide unless the user has since switched into Design mode.
      if (!modePanel.classList.contains('active') && sdsContainer) {
        sdsContainer.style.display = 'none'
      }
    }
  }
}
